import { useEffect, useState, useCallback, useRef, useLayoutEffect } from 'react'
import { authedFetch } from '../api'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchFailures, fetchMoreFailures, createFailure } from '../slices/failuresSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import FailureCardsHeader from '../components/FailureCardsHeader'
import FailuresGrid from '../components/FailuresGrid'
import NewFailureDialog from '../components/NewFailureDialog'
import TagFilterBar from '../components/TagFilterBar'
import './FailuresPage.css'

function FailuresPage() {
  const dispatch = useAppDispatch()
  const { items: failures, loading, error, hasMore, page, total, tags, agents, agentTotals } = useAppSelector((state) => state.failures)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const pageStickyHeaderRef = useRef<HTMLElement>(null)
  const [failuresGridStickyTop, setFailuresGridStickyTop] = useState(0)

  useLayoutEffect(() => {
    const el = pageStickyHeaderRef.current
    if (!el) return
    const update = () => setFailuresGridStickyTop(el.offsetHeight)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim())
    }, 300)
  }, [])

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  useEffect(() => {
    dispatch(fetchFailures({ tag: selectedTag, search: debouncedSearch || null }))
  }, [dispatch, selectedTag, debouncedSearch])

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true)
    await dispatch(fetchMoreFailures({ page: page + 1, tag: selectedTag, search: debouncedSearch || null }))
    setLoadingMore(false)
  }, [dispatch, page, selectedTag, debouncedSearch])

  const handleCreateFailure = useCallback(
    async (data: {
      title: string
      tag_name: string
      github_repository: string
      branch: string
      failing_changeset: string
      fixed_changeset: string
      test_file: string
    }) => {
      await dispatch(createFailure(data)).unwrap()
    },
    [dispatch]
  )

  const handleCreateTag = useCallback(async (name: string) => {
    const res = await authedFetch('/api/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    })
    if (!res.ok) throw new Error('Failed to create tag')
    await dispatch(fetchFailures({ tag: selectedTag, search: debouncedSearch || null }))
  }, [dispatch, selectedTag, debouncedSearch])

  return (
    <div data-testid="failures-page" className="failures-page">
      <SidebarNavigation />

      <main
        className="app-page-main"
        style={{
          ['--failures-grid-sticky-top' as string]: `${failuresGridStickyTop > 0 ? failuresGridStickyTop : 200}px`,
        }}
      >
        <header ref={pageStickyHeaderRef} className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <FailureCardsHeader
              count={total}
              onAddFailure={() => setDialogOpen(true)}
            />

            <TagFilterBar
              tags={tags}
              selectedTag={selectedTag}
              onSelectTag={setSelectedTag}
              onCreateTag={handleCreateTag}
            />

            <div className="failures-search-bar">
              <input
                type="text"
                data-testid="failures-search-input"
                className="failures-search-input"
                placeholder="Filter by short ID or title..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="failures-search-clear"
                  data-testid="failures-search-clear"
                  onClick={() => { setSearchInput(''); setDebouncedSearch('') }}
                  title="Clear filter"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </header>

        {loading && (
          <div data-testid="failures-loading" className="failures-loading">
            Loading failures...
          </div>
        )}

        {error && (
          <div data-testid="failures-error" className="failures-error">
            {error}
          </div>
        )}

        {!loading && !error && failures.length === 0 && (
          <div data-testid="failures-empty" className="failures-empty">
            {debouncedSearch ? `No failures matching "${debouncedSearch}".` : selectedTag ? `No failures with tag "${selectedTag}".` : 'No failures found. Add a failure to get started.'}
          </div>
        )}

        {!loading && failures.length > 0 && (
          <FailuresGrid failures={failures} agents={agents} agentTotals={agentTotals} tagFilter={selectedTag} searchFilter={debouncedSearch || null} />
        )}

        {hasMore && !loading && (
          <div className="app-load-more-wrap">
            <button
              data-testid="load-more-failures"
              type="button"
              className="app-load-more-btn load-more-btn"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading...' : `Load More (${failures.length} of ${total})`}
            </button>
          </div>
        )}
      </main>

      <NewFailureDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreateFailure}
      />
    </div>
  )
}

export default FailuresPage
